import { Component, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { lastValueFrom, map } from 'rxjs';
import { IntegrationControllerService } from 'src/app/admin-api/api/integrationController.service';
import { ShopOrderService } from 'src/app/connect-api/api/shop/shop-order.service';
import { ShopPendingOrderService } from 'src/app/connect-api/api/shop/shop-pending-order.service';
import { TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  FailExportGroup,
  FailExportGroupBy,
  groupFailExports
} from './group-fail-export.util';
import {
  FailExportRow,
  mapPendingOrdersToFailExports
} from './map-pending-order-fail-export.util';

@Component({
  selector: 'app-fail-export',
  templateUrl: './fail-export.component.html',
  styleUrl: './fail-export.component.scss'
})
export class FailExportComponent implements OnInit {
  private integrationService: IntegrationControllerService = inject(
    IntegrationControllerService
  );
  private shopPendingOrderService = inject(ShopPendingOrderService);
  private shopOrderService = inject(ShopOrderService);
  private title = inject(Title);

  failExport: FailExportRow[] | undefined;
  groupedFailExport: FailExportGroup[] = [];

  groupByControl = new FormControl<FailExportGroupBy>('none', {
    nonNullable: true
  });

  groupByOptions: Array<{ label: string; value: FailExportGroupBy }> = [
    { label: 'Nenhum', value: 'none' },
    { label: 'Por robô', value: 'robot' },
    { label: 'Por motivo', value: 'reason' }
  ];

  detailCols: Array<TableColumn> = [
    new TableColumn('Robô', 'descriptionBot', true, 'text'),
    new TableColumn.Builder()
      .setHeader('SubscriberId')
      .setField('subscriberId')
      .setType('number')
      .setRouterLink('/users/subscribers/')
      .setRouterLinkFieldName('subscriberId')
      .build(),
    new TableColumn('EditionId', 'editionId', true, 'number'),
    new TableColumn.Builder()
      .setHeader('Pedido')
      .setField('orderNumber')
      .setDisplayFunction((row: FailExportRow) =>
        row.orderNumber || row.shippingOrderId
      )
      .setRouterLink((row: FailExportRow) => {
        if (row.orderId) {
          return `/shop-orders/order/${row.orderId}`;
        }
        if (row.shippingOrderId) {
          return `/products/shipping-orders/${row.shippingOrderId}`;
        }
        return '';
      })
      .setLinkActive((row: FailExportRow) =>
        Boolean(row.orderId || row.shippingOrderId)
      )
      .build(),
    new TableColumn('Motivo', 'payloadError', true, 'text'),
    new TableColumn('Etapa', 'step', true, 'text'),
    new TableColumn('Data da tentativa', 'dateCreated', true, 'date')
  ];

  groupCols: Array<TableColumn> = [
    new TableColumn('Grupo', 'groupLabel', true, 'text'),
    new TableColumn('Quantidade', 'count', true, 'number')
  ];

  get isGrouped(): boolean {
    return this.groupByControl.value !== 'none';
  }

  ngOnInit(): void {
    this.title.setTitle('Integrações - Falhas de exportação');
    this.fetchFailExport();
  }

  onGroupByChange(): void {
    this.applyGrouping();
  }

  async fetchFailExport(): Promise<void> {
    try {
      LoaderService.showLoader();
      const [apiResult, pendingResult] = await Promise.allSettled([
        lastValueFrom(
          this.integrationService
            .findAllFailExport()
            .pipe(map((data) => data.result ?? []))
        ),
        this.fetchStoreActivationFailures()
      ]);

      const apiFailures =
        apiResult.status === 'fulfilled' ? apiResult.value : [];
      const pendingFailures =
        pendingResult.status === 'fulfilled' ? pendingResult.value : [];

      if (apiResult.status === 'rejected') {
        AppDialogService.showErrorDialog(apiResult.reason);
      }
      if (pendingResult.status === 'rejected') {
        AppDialogService.showErrorDialog(pendingResult.reason);
      }

      this.failExport = [...apiFailures, ...pendingFailures];
      this.applyGrouping();
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async fetchStoreActivationFailures(): Promise<FailExportRow[]> {
    const pendingOrders = await this.shopPendingOrderService.findAll();
    const rows = mapPendingOrdersToFailExports(pendingOrders);
    await this.attachStoreActivationOrderNumbers(rows);
    return rows;
  }

  private async attachStoreActivationOrderNumbers(
    rows: FailExportRow[]
  ): Promise<void> {
    await Promise.all(
      rows.map(async (row) => {
        if (!row.orderId) {
          return;
        }
        row.orderNumber = await this.resolveOrderNumber(row.orderId);
      })
    );
  }

  private async resolveOrderNumber(orderId: string): Promise<string> {
    const order = await this.shopOrderService
      .getOrderById(orderId)
      .catch(() => undefined);
    return order?.orderNumber || orderId;
  }

  private applyGrouping(): void {
    this.groupedFailExport = groupFailExports(
      this.failExport,
      this.groupByControl.value
    );
  }
}
